<script setup>
/**
 * CatalogItemCard Component
 * Displays a single catalog item as a card with image and name
 * Supports horizontal layout (2-3 per row desktop, 1 per row mobile)
 * and vertical layout (4-6 per row desktop, 2 per row mobile)
 */
import { computed, ref, onMounted, watch, nextTick } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { usePromotionsStore } from '../../../app/store/promotions.js'
import { useAmountDiscountsStore } from '../../../app/store/amountDiscounts.js'
import { useCartStore } from '../../../app/store/cart.js'
import { useCatalogStore } from '../../../app/store/catalog.js'
import { useAssortmentRequestStore } from '../../../app/store/assortmentRequest.js'
import {
  getAllMakatsWithParitMerakez,
  applyDiscountPercent,
  pickAnticipatedDiscountTier,
} from '../../../core/utils/discountCalc.js'
import { getPromoSteps } from '../../../core/utils/promoCalc.js'
import { requiresBoxMultiples, parseBoxSize } from './item-dialog/itemDialogUtils.js'
import { ROUTES } from '../../../core/config/constants.js'

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  itemsPerRow: {
    type: Number,
    default: 4,
  },
  isMobile: {
    type: Boolean,
    default: false,
  },
  isTablet: {
    type: Boolean,
    default: false,
  },
  isGridMode: {
    type: Boolean,
    default: false,
  },
  showPrices: {
    type: Boolean,
    default: true,
  },
  tutorialConfig: {
    type: Object,
    default: null,
  },
  goalMakats: {
    type: Set,
    default: null,
  },
})

const emit = defineEmits(['click'])

// Goal highlight: check if this item's makat is in an active goal
const isGoalItem = computed(() => {
  if (!props.goalMakats || props.goalMakats.size === 0) return false
  return props.goalMakats.has(String(props.item.makat))
})

// Promotions store for promo styling
const promotionsStore = usePromotionsStore()

// Amount discounts store for discount badge
const amountDiscountsStore = useAmountDiscountsStore()

// Cart store for qty indicator
const cartStore = useCartStore()

// Catalog store for multi-item discount group detection
const catalogStore = useCatalogStore()

// Assortment request store for NIA selection
const assortmentRequestStore = useAssortmentRequestStore()

// Router for navigation to quantity discount page
const router = useRouter()
const route = useRoute()

// Qty in cart for this item (base qty, not split display lines)
const qtyInCart = computed(() => {
  return cartStore.linesByMakat[props.item.makat]?.qty || 0
})

// Not in assortment flag
const isNotInAssortment = computed(() => props.item._notInAssortment === true)

// NIA selection state
const isNiaSelected = computed(() => {
  return isNotInAssortment.value && assortmentRequestStore.has(props.item.makat)
})

// Handle card click
function handleCardClick() {
  // Toggle selection for not-in-assortment items
  if (isNotInAssortment.value) {
    assortmentRequestStore.toggle(props.item.makat)
    return
  }

  // Check if this item is part of a multi-item quantity discount group
  if (isMultiItemDiscountGroup.value) {
    const clientMakat = route.params.clientMakat
    if (clientMakat) {
      router.push({
        name: ROUTES.AGENTS_QUANTITY_DISCOUNT,
        params: { clientMakat, paritMerakez: props.item.paritMerakez },
      })
    } else {
      router.push({
        name: ROUTES.QUANTITY_DISCOUNT,
        params: { paritMerakez: props.item.paritMerakez },
      })
    }
    return
  }

  // Normal behavior - emit click to open dialog
  emit('click', props.item.makat)
}

// Check if image is valid
function hasValidImage(item) {
  return item.image && typeof item.image === 'string' && item.image.trim().length > 0
}

/**
 * Get eligible promotions for this item
 * Excludes first_order promos if user is not eligible
 */
const itemPromotions = computed(() => {
  return promotionsStore.getEligiblePromosForMakat(props.item.makat) || []
})

/**
 * Get promotion style for item
 * Priority: first_order (gold) > weekly (blue) > regular (red) > none
 * @returns {'first_order' | 'weekly' | 'regular' | 'none'}
 */
const promoStyle = computed(() => {
  if (itemPromotions.value.length === 0) {
    return 'none'
  }

  // Check if any promotion is first_order (gold wins)
  const hasFirstOrder = itemPromotions.value.some((p) => p.first_order === true)
  if (hasFirstOrder) {
    return 'first_order'
  }

  // Weekly benefit (blue) beats regular
  const hasWeekly = itemPromotions.value.some((p) => p.weeklyBenefit === true)
  if (hasWeekly) {
    return 'weekly'
  }

  // Has regular promotion(s)
  return 'regular'
})

/**
 * Check if item is out of stock (showOnWeb === 'H')
 */
const isOutOfStock = computed(() => {
  return props.item.showOnWeb === 'H'
})

/**
 * Check if item has amount discount (only when NO promo)
 */
const hasAmountDiscount = computed(() => {
  // If item has any promo, don't show amount discount
  if (itemPromotions.value.length > 0) return false
  // Check by paritMerakez (PARTNAME)
  return amountDiscountsStore.hasAmountDiscount(props.item.paritMerakez)
})

/**
 * Check if item is part of a multi-item quantity discount group
 * A multi-item group means 2+ items share the same paritMerakez
 */
const isMultiItemDiscountGroup = computed(() => {
  // If item has any promo, promotions take priority - don't navigate to quantity discount page
  if (itemPromotions.value.length > 0) return false
  // Must have a paritMerakez
  if (!props.item.paritMerakez) return false
  // Must have amount discount for this paritMerakez
  if (!amountDiscountsStore.hasAmountDiscount(props.item.paritMerakez)) return false
  // Check if there are multiple items with this paritMerakez
  const makats = getAllMakatsWithParitMerakez(props.item.paritMerakez, catalogStore.itemsByMakat)
  return makats.length > 1
})

/**
 * Check if a step has a valid price (not null, not empty string)
 */
function hasValidPrice(step) {
  return step?.price != null && step.price !== ''
}

/**
 * Count valid steps in a madregot promotion
 */
function countSteps(promo) {
  let count = 0
  if (hasValidPrice(promo.step_1)) count++
  if (hasValidPrice(promo.step_2)) count++
  if (hasValidPrice(promo.step_3)) count++
  if (hasValidPrice(promo.step_4)) count++
  if (hasValidPrice(promo.step_5)) count++
  return count
}

/**
 * Get madregot promotion info for price display
 * Returns: { isMadregot: boolean, hasMultipleSteps: boolean, singleStepPrice: number|null, isFirstOrder: boolean }
 */
const madregotInfo = computed(() => {
  // Find madregot promotion for this item
  const madregotPromo = itemPromotions.value.find((p) => p.saleType === 'madregot')

  if (!madregotPromo) {
    return {
      isMadregot: false,
      hasMultipleSteps: false,
      singleStepPrice: null,
      isFirstOrder: false,
    }
  }

  const stepCount = countSteps(madregotPromo)
  const isFirstOrder = madregotPromo.first_order === true

  if (stepCount > 1) {
    return { isMadregot: true, hasMultipleSteps: true, singleStepPrice: null, isFirstOrder }
  }

  if (stepCount === 1) {
    // Get the single step's price (find the one with valid price)
    let singleStepPrice = null
    if (hasValidPrice(madregotPromo.step_1)) singleStepPrice = madregotPromo.step_1.price
    else if (hasValidPrice(madregotPromo.step_2)) singleStepPrice = madregotPromo.step_2.price
    else if (hasValidPrice(madregotPromo.step_3)) singleStepPrice = madregotPromo.step_3.price
    else if (hasValidPrice(madregotPromo.step_4)) singleStepPrice = madregotPromo.step_4.price
    else if (hasValidPrice(madregotPromo.step_5)) singleStepPrice = madregotPromo.step_5.price

    return { isMadregot: true, hasMultipleSteps: false, singleStepPrice, isFirstOrder }
  }

  return { isMadregot: false, hasMultipleSteps: false, singleStepPrice: null, isFirstOrder: false }
})

/**
 * Get the display price (considers madregot single step override)
 */
const displayPrice = computed(() => {
  if (
    madregotInfo.value.isMadregot &&
    !madregotInfo.value.hasMultipleSteps &&
    madregotInfo.value.singleStepPrice != null
  ) {
    return madregotInfo.value.singleStepPrice
  }
  return props.item.price
})

// Determine if horizontal layout should be used
// Mobile/Tablet: 1 card = horizontal, 2 cards = vertical
// PC: 2-3 cards = horizontal, 4-6 = vertical
const isHorizontal = computed(() => {
  if (props.isMobile || props.isTablet) {
    return props.itemsPerRow === 1
  }
  return props.itemsPerRow <= 3
})

// Use short button text on PC with 3 cards per row (space is limited)
const useShortButtonText = computed(() => {
  return !props.isMobile && !props.isTablet && props.itemsPerRow === 3
})

// Card classes based on layout mode and promo style
// Breakpoints: Mobile < 768px | Tablet 768-1279px | PC >= 1280px
const cardClasses = computed(() => ({
  'item-card': true,
  'horizontal-layout': isHorizontal.value,
  'vertical-layout': !isHorizontal.value,
  // PC classes (>= 1280px)
  'desktop-small': !props.isMobile && !props.isTablet && props.itemsPerRow <= 3,
  'desktop-large': !props.isMobile && !props.isTablet && props.itemsPerRow >= 4,
  // Tablet classes (768-1279px)
  'tablet-single': props.isTablet && props.itemsPerRow === 1,
  'tablet-double': props.isTablet && props.itemsPerRow === 2,
  'tablet-triple': props.isTablet && props.itemsPerRow >= 3,
  // Mobile classes (< 768px)
  'mobile-single': props.isMobile && props.itemsPerRow === 1,
  'mobile-double': props.isMobile && props.itemsPerRow === 2,
  'grid-mode': props.isGridMode,
  'promo-regular': promoStyle.value === 'regular' && !isOutOfStock.value,
  'promo-first-order': promoStyle.value === 'first_order' && !isOutOfStock.value,
  'promo-weekly': promoStyle.value === 'weekly' && !isOutOfStock.value,
  'amount-discount': hasAmountDiscount.value && !isOutOfStock.value,
  'out-of-stock': isOutOfStock.value,
  'not-in-assortment': isNotInAssortment.value,
  'nia-selected': isNiaSelected.value,
}))

// Format price to always show 2 decimal places
const formattedPrice = computed(() => {
  const price = displayPrice.value
  if (price == null) return null
  const num = parseFloat(price)
  if (isNaN(num)) return price
  return num.toFixed(2)
})

// Should show "מבצע" badge instead of price
const showMivtzaBadge = computed(() => {
  return madregotInfo.value.isMadregot && madregotInfo.value.hasMultipleSteps
})

// Step price range for madregot with multiple steps (without ₪, handled in template)
const stepPriceRange = computed(() => {
  if (!showMivtzaBadge.value) return null
  const madregotPromo = itemPromotions.value.find((p) => p.saleType === 'madregot')
  if (!madregotPromo) return null
  const steps = getPromoSteps(madregotPromo)
  if (steps.length === 0) return null
  const prices = steps.map((s) => s.price)
  const min = Math.min(...prices)
  const max = Math.max(...prices)
  if (min === max) return { min: min.toFixed(2), max: null }
  return { min: min.toFixed(2), max: max.toFixed(2) }
})

// Discount price range for amount discount items (lowest to highest price based on tiers)
const discountPriceRange = computed(() => {
  if (!hasAmountDiscount.value) return null
  const tiers = amountDiscountsStore.getDiscountTiers(props.item.paritMerakez)
  if (!tiers || tiers.length === 0) return null
  const basePrice = parseFloat(props.item.price)
  if (isNaN(basePrice) || basePrice <= 0) return null

  const prices = tiers.map((t) => applyDiscountPercent(basePrice, t.discountPercent))
  // Box-only items whose first tier fits inside one box can never be bought at
  // the base price — cap the range's top end at the guaranteed first-add tier
  const minAdd = requiresBoxMultiples(props.item) ? parseBoxSize(props.item) || 1 : 1
  const anticipated = pickAnticipatedDiscountTier({ discounts: tiers, totalQty: 0, minAdd })
  prices.push(
    anticipated ? applyDiscountPercent(basePrice, anticipated.discountPercent) : basePrice
  )
  const min = Math.min(...prices)
  const max = Math.max(...prices)
  if (min === max) return { min: min.toFixed(2), max: null }
  return { min: min.toFixed(2), max: max.toFixed(2) }
})

// Should show original price crossed out
const showOriginalPrice = computed(() => {
  return madregotInfo.value.isMadregot && props.item.price != null
})

// Format original price
const formattedOriginalPrice = computed(() => {
  const price = props.item.price
  if (price == null) return null
  const num = parseFloat(price)
  if (isNaN(num)) return price
  return num.toFixed(2)
})

// Dynamic font sizing for horizontal layout
const nameRef = ref(null)
const nameFontSize = ref(null)

// Base font sizes (in pixels) for horizontal layouts
const BASE_FONT_SIZE = 18 // --font-size-lg equivalent
const MIN_FONT_SIZE = 11

function adjustNameFontSize() {
  if (!nameRef.value) return

  const el = nameRef.value

  // For vertical layouts, clear inline style and use CSS
  if (!isHorizontal.value) {
    el.style.fontSize = ''
    nameFontSize.value = null
    return
  }

  // For horizontal layouts, dynamically adjust font size
  // Reset to base size first
  el.style.fontSize = `${BASE_FONT_SIZE}px`

  let currentSize = BASE_FONT_SIZE

  // Reduce font size until text fits or we hit minimum
  while (el.scrollWidth > el.clientWidth && currentSize > MIN_FONT_SIZE) {
    currentSize -= 0.5
    el.style.fontSize = `${currentSize}px`
  }

  nameFontSize.value = currentSize
}

onMounted(() => {
  nextTick(() => {
    adjustNameFontSize()
  })
})

// Re-adjust when item or layout changes
watch([() => props.item.name, isHorizontal, () => props.itemsPerRow], () => {
  nextTick(() => {
    adjustNameFontSize()
  })
})
</script>

<template>
  <div v-tutorial="tutorialConfig" :class="cardClasses" @click="handleCardClick">
    <!-- NIA Selection Checkmark -->
    <Transition name="check-pop">
      <div v-if="isNiaSelected" class="nia-selection-check">
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="white"
          stroke-width="3"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </div>
    </Transition>

    <!-- Image Area -->
    <div class="card-image-wrapper">
      <!-- Makat Badge -->
      <div class="makat-badge">{{ item.makat }}</div>

      <!-- כשל"פ Sticker (top left, over image) -->
      <div v-if="item.kosher_rabanut === '2'" class="kashlap-sticker">כשל"פ</div>

      <!-- Origin Label (top right) -->
      <div v-if="item.origin" class="origin-label">{{ item.origin }}</div>

      <!-- Rabanut Badge (bottom left, above barcode) -->
      <img
        v-if="item.kosher_rabanut === '1' || item.kosher_rabanut === '2'"
        src="../../../assets/utils/RabanutSign.png"
        alt="באישור רבנות ראשית בלבד"
        class="rabanut-badge"
      />

      <!-- Barcode Overlay -->
      <div v-if="item.barcode" class="barcode-overlay">
        <span>{{ item.barcode }}</span>
      </div>

      <!-- Qty in Cart Badge (centered on image) -->
      <Transition name="qty-badge">
        <div v-if="qtyInCart > 0" class="qty-in-cart-badge">
          {{ qtyInCart }}
        </div>
      </Transition>

      <img
        v-if="hasValidImage(item)"
        :src="item.image"
        :alt="item.name"
        class="card-image"
        loading="lazy"
      />
      <div v-else class="card-image-placeholder">
        <svg
          viewBox="0 0 24 24"
          width="32"
          height="32"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
        >
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <circle cx="8.5" cy="8.5" r="1.5" />
          <path d="M21 15l-5-5L5 21" />
        </svg>
        <span>אין תמונה</span>
      </div>

      <!-- Promo Type Bar (overlaid at bottom of image) -->
      <div v-if="promoStyle === 'first_order'" class="promo-bar first-order">לקוחות חדשים</div>
      <div v-else-if="promoStyle === 'weekly'" class="promo-bar weekly">הטבה שבועית</div>
      <div v-else-if="promoStyle === 'regular'" class="promo-bar regular">מבצע</div>
    </div>

    <!-- Card Content -->
    <div class="card-content">
      <!-- Item Name -->
      <div ref="nameRef" class="card-name" :class="{ 'goal-name': isGoalItem }">
        {{ item.name || 'ללא שם' }}
      </div>

      <!-- Stats Area -->
      <div class="card-stats">
        <div class="stats-rows">
          <div v-if="item.boxSize" class="stat-row">
            <span class="stat-label">כמות במארז:</span>
            <span class="stat-value">{{ item.boxSize }}</span>
          </div>
          <div class="stat-row">
            <span class="stat-label">הזמנה ביחידות:</span>
            <span
              class="stat-value"
              :class="item.onlyBox === 1 || item.onlyBox === '1' ? 'stat-yes' : 'stat-no'"
            >
              {{ item.onlyBox === 1 || item.onlyBox === '1' ? 'כן' : 'לא' }}
            </span>
          </div>
          <div v-if="item.kosher_type" class="stat-row">
            <span class="stat-label">כשרות:</span>
            <span class="stat-value">{{ item.kosher_type }}</span>
          </div>
          <div v-if="item.returns === '1'" class="stat-row stat-row-warning">
            <span class="stat-value-warning">פריט ללא זכות החזרה</span>
          </div>
        </div>

        <!-- Price Range (madregot with multiple steps) -->
        <div v-if="stepPriceRange && showPrices" class="price-range-row">
          <span>טווח מחירים: </span>
          <span class="price-range-currency">₪</span>{{ stepPriceRange.min }}
          <template v-if="stepPriceRange.max">
            - <span class="price-range-currency">₪</span>{{ stepPriceRange.max }}
          </template>
        </div>

        <!-- Price Range (amount discount tiers) -->
        <div v-if="discountPriceRange && showPrices" class="price-range-row discount-price-range">
          <span>טווח מחירים: </span>
          <span class="price-range-currency">₪</span>{{ discountPriceRange.min }}
          <template v-if="discountPriceRange.max">
            - <span class="price-range-currency">₪</span>{{ discountPriceRange.max }}
          </template>
        </div>

        <!-- Not In Assortment Label (vertical layout) -->
        <div v-if="!isHorizontal && isNotInAssortment" class="not-in-assortment-label">
          <span>פריט לא במגוון</span>
        </div>
        <!-- Out of Stock Label (vertical layout) -->
        <div v-else-if="!isHorizontal && isOutOfStock" class="out-of-stock-label">
          <span>חסר זמנית במלאי</span>
        </div>
        <!-- Add to Cart Button (vertical layout - after stats) -->
        <button
          v-else-if="!isHorizontal && showPrices"
          class="add-to-cart-btn"
          :class="{ 'discount-btn': hasAmountDiscount }"
          @click.stop="handleCardClick"
        >
          <svg
            v-if="!hasAmountDiscount"
            class="add-icon"
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
          >
            <path d="M12 5v14M5 12h14" />
          </svg>
          <span>{{ hasAmountDiscount ? 'לחצו לפירוט הנחות' : 'הוסף לסל' }}</span>
        </button>

        <!-- Bottom Row: Price + Add to Cart (horizontal layout) -->
        <div class="bottom-row">
          <!-- Not In Assortment (horizontal layout) -->
          <template v-if="isHorizontal && isNotInAssortment">
            <div class="not-in-assortment-label">
              <span>פריט לא במגוון</span>
            </div>
          </template>
          <!-- Out of Stock (horizontal layout) -->
          <template v-else-if="isHorizontal && isOutOfStock">
            <div class="out-of-stock-label">
              <span>חסר זמנית במלאי</span>
            </div>
          </template>
          <template v-else>
            <!-- Price Box -->
            <div class="price-area">
              <!-- Guest: login to see prices -->
              <div v-if="!showPrices" class="price-box price-box-guest">
                <span class="guest-price-text">התחבר לצפייה במחירים</span>
              </div>
              <!-- Amount discount: show "תלוי בכמות" -->
              <div v-else-if="hasAmountDiscount" class="price-box price-box-discount">
                <span class="discount-text">הנחת כמות</span>
              </div>
              <!-- Original price crossed out (when promotion) -->
              <template v-else>
                <div v-if="showOriginalPrice" class="price-box-original">
                  <span class="price-currency">₪</span>
                  <span class="price-value">{{ formattedOriginalPrice }}</span>
                  <span class="price-strike"></span>
                </div>

                <!-- Promo price or badge -->
                <div
                  v-if="showMivtzaBadge"
                  class="price-box"
                  :class="madregotInfo.isFirstOrder ? 'price-box-first-order' : 'price-box-mivtza'"
                >
                  <span class="mivtza-text">מבצע</span>
                </div>
                <div
                  v-else-if="displayPrice"
                  class="price-box"
                  :class="{
                    'price-box-mivtza': madregotInfo.isMadregot && !madregotInfo.isFirstOrder,
                    'price-box-first-order': madregotInfo.isMadregot && madregotInfo.isFirstOrder,
                  }"
                >
                  <span class="price-currency">₪</span>
                  <span class="price-value">{{ formattedPrice }}</span>
                </div>
              </template>
            </div>

            <!-- Add to Cart Button (horizontal layout - inline with price) -->
            <button
              v-if="isHorizontal && showPrices"
              class="add-to-cart-btn add-to-cart-inline"
              :class="{ 'discount-btn': hasAmountDiscount }"
              @click.stop="handleCardClick"
            >
              <svg
                v-if="!hasAmountDiscount"
                class="add-icon"
                viewBox="0 0 24 24"
                width="16"
                height="16"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
              >
                <path d="M12 5v14M5 12h14" />
              </svg>
              <span>{{
                hasAmountDiscount ? 'לחצו לפירוט הנחות' : useShortButtonText ? 'הוסף' : 'הוסף לסל'
              }}</span>
            </button>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* ============================================
   Base Card Styles
   ============================================ */
.item-card {
  position: relative;
  display: flex;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  overflow: hidden;
  transition: all var(--transition-fast);
  cursor: pointer;
}

/* Promotion gradient overlay (via ::before) */
.item-card::before {
  content: '';
  position: absolute;
  inset: 0;
  opacity: 0;
  pointer-events: none;
  z-index: 0;
  transition: opacity var(--transition-fast);
}

/* Regular promotion - clean style */
.item-card.promo-regular::before {
  background: none;
}

/* First order (new customers) promotion - premium gold gradient */
.item-card.promo-first-order::before {
  background:
    radial-gradient(ellipse at top right, rgba(250, 204, 21, 0.22) 0%, transparent 50%),
    radial-gradient(ellipse at bottom left, rgba(202, 138, 4, 0.12) 0%, transparent 50%),
    linear-gradient(
      160deg,
      rgba(254, 249, 195, 0.25) 0%,
      rgba(253, 224, 71, 0.1) 30%,
      transparent 60%
    );
}

.item-card.promo-first-order {
  border-color: rgba(234, 179, 8, 0.35);
  box-shadow:
    0 0 0 1px rgba(234, 179, 8, 0.2),
    inset 0 1px 0 rgba(254, 249, 195, 0.5),
    0 4px 12px -2px rgba(234, 179, 8, 0.12);
}

/* Promo Type Bar (overlaid at bottom of image) */
.promo-bar {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 5px 0;
  color: white;
  font-size: 12px;
  font-weight: var(--font-weight-bold);
  text-align: center;
  z-index: 2;
  letter-spacing: 0.5px;
}

.promo-bar.first-order {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
}

.promo-bar.regular {
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
}

.promo-bar.weekly {
  background: var(--color-info);
}

/* Weekly benefit promotion - subtle info-blue frame */
.item-card.promo-weekly::before {
  background: none;
}

.item-card.promo-weekly {
  border-color: color-mix(in srgb, var(--color-info) 35%, transparent);
  box-shadow: 0 0 0 1px color-mix(in srgb, var(--color-info) 18%, transparent);
}

/* Amount discount - clean style */
.item-card.amount-discount::before {
  background: none;
}

.item-card.amount-discount {
  border-color: rgba(34, 197, 94, 0.25);
}

.item-card:hover {
  border-color: var(--color-primary-light);
  box-shadow: var(--shadow-md);
  transform: translateY(-2px);
}

.item-card:active {
  transform: scale(0.98);
}

/* ============================================
   Vertical Layout (Default - 4-5-6 per row)
   ============================================ */
.vertical-layout {
  flex-direction: column;
}

.vertical-layout .card-image-wrapper {
  width: 100%;
  flex-shrink: 0;
}

.vertical-layout .card-content {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

/* ============================================
   Horizontal Layout (2-3 per row desktop, 1 per row mobile)
   ============================================ */
.horizontal-layout {
  flex-direction: row-reverse; /* Image on right for RTL */
}

.horizontal-layout .card-image-wrapper {
  width: 30%;
  flex-shrink: 0;
  height: 100%;
}

.horizontal-layout .card-content {
  width: 70%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

/* ============================================
   Image Wrapper
   ============================================ */
.card-image-wrapper {
  position: relative;
  z-index: 1;
  background: #ffffff;
  overflow: hidden;
}

/* First-order ("לקוחות חדשים" / new customers) promo:
   sunburst backdrop sits under the product image, matching PromotionCard.
   Only visible on background-removed (transparent) product images. */
.promo-first-order .card-image-wrapper {
  background-image: url('../../../assets/card-backgrounds/02-sunburst-rays.svg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.card-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: var(--spacing-xs);
}

/* Goal Name Shimmer */
.card-name.goal-name {
  position: relative;
  overflow: hidden;
  background: linear-gradient(
    135deg,
    rgba(251, 191, 36, 0.12) 0%,
    rgba(245, 158, 11, 0.22) 40%,
    rgba(251, 191, 36, 0.1) 60%,
    rgba(245, 158, 11, 0.16) 100%
  );
  border-radius: 4px;
}

.card-name.goal-name::after {
  content: '';
  position: absolute;
  top: -50%;
  left: -60%;
  width: 40%;
  height: 200%;
  background: linear-gradient(
    105deg,
    transparent 30%,
    rgba(251, 191, 36, 0.28) 50%,
    transparent 70%
  );
  animation: goal-sweep 3s ease-in-out infinite;
}

@keyframes goal-sweep {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(500%);
  }
}

/* Image Placeholder */
.card-image-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  gap: var(--spacing-xs);
  color: var(--text-muted);
  background: var(--bg-tertiary);
}

.card-image-placeholder svg {
  opacity: 0.5;
}

.card-image-placeholder span {
  font-size: 12px;
  opacity: 0.7;
}

/* Makat Badge */
.makat-badge {
  position: absolute;
  top: 6px;
  left: 6px;
  padding: 2px 6px;
  background: var(--color-primary);
  color: white;
  font-weight: 700;
  font-size: 11px;
  font-weight: var(--font-weight-medium);
  border-radius: var(--border-radius-sm);
  z-index: 1;
  font-family: var(--font-family-mono, monospace);
  letter-spacing: 0.5px;
}

/* כשל"פ Sticker (top left, over image) */
.kashlap-sticker {
  position: absolute;
  top: 0;
  right: 0;
  padding: 3px 8px;
  background: linear-gradient(135deg, #0d9488, #0f766e);
  color: white;
  font-size: 11px;
  font-weight: var(--font-weight-bold);
  z-index: 2;
  border-radius: 0 0 0 var(--border-radius-sm);
  letter-spacing: 0.3px;
  line-height: 1.3;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

/* Origin Label (top right, no background) */
.origin-label {
  position: absolute;
  top: 6px;
  right: 6px;
  padding: 2px 4px;
  color: var(--text-secondary);
  font-size: 10px;
  font-weight: var(--font-weight-medium);
  z-index: 1;
  text-shadow: 0 1px 2px rgba(255, 255, 255, 0.8);
}

/* Rabanut Badge (bottom left, above barcode) */
.rabanut-badge {
  position: absolute;
  bottom: 40px;
  left: 4px;
  width: 32px;
  height: auto;
  z-index: 1;
  opacity: 0.85;
  pointer-events: none;
  background: white;
  border-radius: 4px;
  padding: 2px;
}

/* Barcode Overlay */
.barcode-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3px 6px;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  font-size: 10px;
  font-family: var(--font-family-mono, monospace);
  letter-spacing: 0.5px;
  z-index: 1;
}

.barcode-overlay span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Qty in Cart Badge (centered on image) */
.qty-in-cart-badge {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
  pointer-events: none;

  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  height: 40px;
  padding: 0 12px;

  background: rgba(255, 255, 255, 0.95);
  border: 2px solid var(--color-primary);
  border-radius: var(--border-radius-full);
  box-shadow:
    0 4px 12px rgba(0, 0, 0, 0.15),
    0 0 0 4px rgba(99, 102, 241, 0.1);

  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  color: var(--color-primary);
}

/* Qty badge animation */
.qty-badge-enter-active {
  transition: all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.qty-badge-leave-active {
  transition: all 0.2s ease-out;
}

.qty-badge-enter-from {
  opacity: 0;
  transform: translate(-50%, -50%) scale(0.5);
}

.qty-badge-leave-to {
  opacity: 0;
  transform: translate(-50%, -50%) scale(0.8);
}

/* ============================================
   Card Content
   ============================================ */
.card-content {
  position: relative;
  z-index: 1;
}

.promo-first-order .card-content {
  background:
    radial-gradient(ellipse at top right, rgba(250, 204, 21, 0.22) 0%, transparent 50%),
    radial-gradient(ellipse at bottom left, rgba(202, 138, 4, 0.12) 0%, transparent 50%),
    linear-gradient(
      160deg,
      rgba(254, 249, 195, 0.25) 0%,
      rgba(253, 224, 71, 0.1) 30%,
      transparent 60%
    );
}

.card-name {
  padding: var(--spacing-sm) var(--spacing-md);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--text-primary);
  text-align: center;
  line-height: 1.3;
}

/* Horizontal layout: single line, JS handles font sizing */
.horizontal-layout .card-name {
  white-space: nowrap;
  overflow: hidden;
}

/* Stats Area */
.card-stats {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 0 var(--spacing-sm) var(--spacing-sm);
  overflow: visible;
  position: relative;
  min-height: 0;
}

/* Vertical layout: stats area needs to fill and push content to bottom */
.vertical-layout .card-stats {
  justify-content: flex-start;
}

/* Vertical layout: reorder elements so button is at very bottom */
.vertical-layout .stats-rows {
  order: 1;
  flex-shrink: 0;
}

.vertical-layout .bottom-row {
  order: 3;
  margin-top: auto;
  justify-content: center;
  padding: 4px;
  margin-left: -4px;
  margin-right: -4px;
}

.vertical-layout .add-to-cart-btn {
  order: 4;
  margin-top: var(--spacing-xs);
  flex-shrink: 0;
}

.stats-rows {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.stat-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-size: 11px;
  line-height: 1.3;
}

.stat-label {
  color: var(--text-muted);
  flex-shrink: 0;
}

.stat-value {
  color: var(--text-primary);
  font-weight: var(--font-weight-medium);
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.stat-yes {
  color: var(--color-success, #22c55e);
}

.stat-no {
  color: var(--color-error, #ef4444);
}

.stat-row-warning {
  margin-top: 2px;
}

.stat-value-warning {
  color: #dc2626;
  font-weight: var(--font-weight-semibold);
}

/* Price Range Row */
.price-range-row {
  margin-top: auto;
  padding-top: 4px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  color: #dc2626;
  text-align: center;
}

.price-range-currency {
  font-size: 0.8em;
  margin-inline-end: 1px;
}

/* Discount price range - green color to match amount discount theme */
.discount-price-range {
  color: #16a34a;
}

.vertical-layout .price-range-row {
  order: 2;
}

.horizontal-layout .price-range-row {
  margin-top: auto;
}

/* Bottom Row Container */
.bottom-row {
  display: flex;
  flex-direction: row-reverse;
  align-items: stretch;
  justify-content: space-between;
  gap: var(--spacing-sm);
  min-width: 0;
  overflow: visible;
}

/* Horizontal layout: bottom row needs space for glow effects */
.horizontal-layout .bottom-row {
  margin-top: auto;
  padding: 4px;
  margin-left: -4px;
  margin-right: -4px;
  margin-bottom: -4px;
}

/* Price Area Container */
.price-area {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 1;
  min-width: 0;
}

/* Horizontal layout: price area only as wide as content */
.horizontal-layout .price-area {
  flex-shrink: 0;
  flex-grow: 0;
}

/* Add to Cart Button */
.add-to-cart-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-md);
  background: transparent;
  color: #334155;
  border: none;
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.add-to-cart-btn .add-icon {
  flex-shrink: 0;
}

.add-to-cart-btn:hover {
  color: #1e293b;
}

.add-to-cart-btn:active {
  transform: scale(0.97);
  color: #0f172a;
}

/* Inline Add to Cart (horizontal layout) */
.add-to-cart-btn.add-to-cart-inline {
  flex: 0 0 auto;
  width: auto;
  margin-top: 0;
  padding: var(--spacing-sm) var(--spacing-md);
  align-self: center;
  white-space: nowrap;
}

/* Horizontal layout: smaller fonts to fit in limited space */
.horizontal-layout .bottom-row .price-value {
  font-size: clamp(10px, 2.5vw, var(--font-size-base));
}

.horizontal-layout .bottom-row .price-currency {
  font-size: clamp(8px, 2vw, 11px);
}

.horizontal-layout .bottom-row .mivtza-text {
  font-size: clamp(9px, 2vw, var(--font-size-sm));
}

.horizontal-layout .add-to-cart-btn.add-to-cart-inline {
  font-size: clamp(10px, 2.5vw, var(--font-size-sm));
}

/* Vertical layout: responsive fonts to prevent text wrap */
.vertical-layout .bottom-row .price-value {
  font-size: clamp(10px, 3vw, var(--font-size-base));
}

.vertical-layout .bottom-row .price-currency {
  font-size: clamp(8px, 2.5vw, 11px);
}

.vertical-layout .bottom-row .mivtza-text {
  font-size: clamp(9px, 2.5vw, var(--font-size-sm));
}

.vertical-layout .add-to-cart-btn {
  font-size: clamp(10px, 3vw, var(--font-size-sm));
}

/* Original Price (crossed out) */
.price-box-original {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: var(--spacing-xs) var(--spacing-sm);
  background: rgba(0, 0, 0, 0.06);
  border-radius: var(--border-radius);
  color: var(--text-muted);
  font-weight: var(--font-weight-bold);
  align-self: stretch;
  flex-shrink: 1;
  min-width: 0;
  overflow: visible;
}

.price-box-original .price-currency {
  font-size: 12px;
  font-weight: var(--font-weight-semibold);
  opacity: 0.8;
}

.price-box-original .price-value {
  font-size: var(--font-size-lg);
  letter-spacing: -0.5px;
}

.price-strike {
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 2px;
  background: var(--color-error, #ef4444);
  transform: translateY(-50%) rotate(-8deg);
  opacity: 0.8;
}

/* Price Box */
.price-box {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: var(--spacing-xs) var(--spacing-sm);
  background: linear-gradient(135deg, #334155 0%, #283548 100%);
  border-radius: var(--border-radius);
  color: white;
  font-weight: var(--font-weight-bold);
  box-shadow: 0 2px 8px rgba(40, 53, 72, 0.3);
  flex-shrink: 1;
  min-width: 0;
  overflow: visible;
  align-self: stretch;
}

/* Mivtza (Sale) Badge */
.price-box.price-box-mivtza {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  box-shadow: 0 1px 4px rgba(220, 38, 38, 0.2);
  padding: var(--spacing-xs) var(--spacing-md);
}

/* First Order (New Customers) Badge */
.price-box.price-box-first-order {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  box-shadow: 0 1px 4px rgba(245, 158, 11, 0.2);
  padding: var(--spacing-xs) var(--spacing-md);
}

.mivtza-text {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.5px;
  white-space: nowrap;
}

/* Amount Discount Price Box */
.price-box.price-box-discount {
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  box-shadow: 0 2px 8px rgba(34, 197, 94, 0.3);
  padding: var(--spacing-xs) var(--spacing-sm);
}

.discount-text {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.3px;
  white-space: nowrap;
}

/* Guest Price Box */
.price-box.price-box-guest {
  background: var(--bg-secondary, #f8fafc);
  border: 1px solid var(--border-color, #e2e8f0);
  padding: var(--spacing-xs) var(--spacing-sm);
  box-shadow: none;
}

.guest-price-text {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  color: var(--text-secondary, #475569);
  white-space: nowrap;
}

/* Discount Button Style */
.add-to-cart-btn.discount-btn {
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%);
  color: #16a34a;
  border: 1px solid rgba(34, 197, 94, 0.3);
}

.add-to-cart-btn.discount-btn:hover {
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.15) 0%, rgba(16, 185, 129, 0.1) 100%);
  color: #15803d;
}

.price-currency {
  font-size: 12px;
  font-weight: var(--font-weight-semibold);
  opacity: 0.9;
  flex-shrink: 0;
  white-space: nowrap;
}

.price-value {
  font-size: var(--font-size-lg);
  letter-spacing: -0.5px;
  white-space: nowrap;
}

/* ============================================
   Desktop Sizes
   ============================================ */
/* Desktop 2-3 per row: horizontal layout */
.desktop-small {
  height: 220px;
}

.desktop-small .card-image-wrapper {
  height: 100%;
}

.desktop-small .card-name {
  font-size: var(--font-size-lg);
}

.desktop-small .card-stats {
  padding: 0 var(--spacing-md) var(--spacing-md);
  gap: 4px;
}

.desktop-small .stat-row {
  font-size: 13px;
  justify-content: flex-start;
}

.desktop-small .price-box {
  padding: var(--spacing-sm) var(--spacing-md);
}

.desktop-small .price-currency {
  font-size: 14px;
}

.desktop-small .price-value {
  font-size: var(--font-size-xl);
}

.desktop-small .add-to-cart-btn.add-to-cart-inline {
  padding: var(--spacing-xs) var(--spacing-sm);
  font-size: clamp(10px, 1.5vw, var(--font-size-sm));
}

/* Desktop 4-5-6 per row: vertical layout */
.desktop-large {
  min-height: 380px;
  height: auto;
}

.desktop-large .card-image-wrapper {
  height: 200px;
}

.desktop-large .card-name {
  font-size: var(--font-size-base);
}

.desktop-large .card-stats {
  gap: 3px;
}

.desktop-large .stat-row {
  font-size: 12px;
}

.desktop-large .price-box {
  padding: 4px 8px;
}

.desktop-large .price-currency {
  font-size: 10px;
}

.desktop-large .price-value {
  font-size: var(--font-size-sm);
}

.desktop-large .add-to-cart-btn {
  padding: var(--spacing-sm) var(--spacing-sm);
  font-size: var(--font-size-sm);
}

/* Desktop-large: responsive product name font to prevent text wrap */
.desktop-large .card-name {
  font-size: clamp(11px, 1.3vw, var(--font-size-base));
}

/* ============================================
   Tablet Sizes (768px - 1279px)
   ============================================ */
/* Tablet 1 per row: horizontal layout */
.tablet-single {
  height: 260px;
}

.tablet-single .card-image-wrapper {
  height: 100%;
}

.tablet-single .card-name {
  font-size: var(--font-size-xl);
}

.tablet-single .card-stats {
  padding: 0 var(--spacing-lg) var(--spacing-lg);
  gap: 6px;
}

.tablet-single .stat-row {
  font-size: 14px;
  justify-content: flex-start;
}

.tablet-single .price-box {
  padding: var(--spacing-sm) var(--spacing-md);
}

.tablet-single .price-currency {
  font-size: 14px;
}

.tablet-single .price-value {
  font-size: var(--font-size-xl);
}

.tablet-single .add-to-cart-btn.add-to-cart-inline {
  padding: var(--spacing-sm) var(--spacing-lg);
  font-size: var(--font-size-base);
}

.tablet-single .bottom-row {
  padding: var(--spacing-sm) 0;
}

/* Tablet 2 per row: vertical layout */
.tablet-double {
  min-height: 420px;
  height: auto;
}

.tablet-double .card-image-wrapper {
  height: 200px;
}

.tablet-double .card-name {
  font-size: var(--font-size-lg);
  padding: var(--spacing-sm) var(--spacing-md);
}

.tablet-double .card-stats {
  padding: 0 var(--spacing-sm) var(--spacing-sm);
  gap: 3px;
}

.tablet-double .stat-row {
  font-size: 12px;
  justify-content: flex-start;
}

.tablet-double .price-box {
  padding: 6px 12px;
}

.tablet-double .price-box-original {
  padding: 6px 12px;
}

.tablet-double .price-currency {
  font-size: 11px;
}

.tablet-double .price-value {
  font-size: var(--font-size-lg);
}

.tablet-double .add-to-cart-btn {
  padding: var(--spacing-md) var(--spacing-md);
  font-size: var(--font-size-base);
}

.tablet-double .bottom-row {
  padding: var(--spacing-sm) 0;
}

/* Tablet 3 per row: vertical layout (matches desktop-large) */
.tablet-triple {
  min-height: 380px;
  height: auto;
}

.tablet-triple .card-image-wrapper {
  height: 200px;
}

.tablet-triple .card-name {
  font-size: clamp(11px, 1.3vw, var(--font-size-base));
}

.tablet-triple .card-stats {
  gap: 3px;
}

.tablet-triple .stat-row {
  font-size: 12px;
}

.tablet-triple .price-box {
  padding: 4px 8px;
}

.tablet-triple .price-currency {
  font-size: 10px;
}

.tablet-triple .price-value {
  font-size: var(--font-size-sm);
}

.tablet-triple .add-to-cart-btn {
  padding: var(--spacing-sm) var(--spacing-sm);
  font-size: var(--font-size-sm);
}

/* ============================================
   Mobile Sizes (< 768px)
   ============================================ */
/* Mobile 1 per row: horizontal layout */
.mobile-single {
  height: 170px;
  border-radius: 12px;
  overflow: hidden;
}

.mobile-single .card-image-wrapper {
  height: 100%;
}

.mobile-single .card-content {
  overflow: hidden;
}

.mobile-single .card-name {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-bold);
  -webkit-line-clamp: 1;
  margin-bottom: 2px;
}

.mobile-single .card-stats {
  padding: 0 var(--spacing-sm) var(--spacing-sm);
  gap: 2px;
}

.mobile-single .stat-row {
  font-size: 11px;
  justify-content: flex-start;
}

.mobile-single .stats-rows .stat-row:nth-child(n + 4) {
  display: none;
}

.mobile-single .price-range-row {
  font-size: 11px;
}

.mobile-single .price-box {
  width: 80px;
  height: 60px;
  padding: 0;
  flex-shrink: 0;
  flex-wrap: nowrap;
  gap: 1px;
}

.mobile-single .price-box.price-box-mivtza,
.mobile-single .price-box.price-box-first-order,
.mobile-single .price-box.price-box-discount,
.mobile-single .price-box.price-box-guest {
  width: auto;
  height: auto;
  padding: var(--spacing-xs) var(--spacing-sm);
}

.mobile-single .bottom-row .price-currency {
  font-size: 11px;
  opacity: 0.8;
  margin-inline-end: 2px;
}

.mobile-single .bottom-row .price-value {
  font-size: 18px;
  line-height: 1;
}

.mobile-single .add-to-cart-btn.add-to-cart-inline {
  padding: var(--spacing-xs) var(--spacing-sm);
  font-size: var(--font-size-sm);
}

.mobile-single .bottom-row {
  padding: 2px 0;
}

.mobile-single .bottom-row .mivtza-text {
  font-size: var(--font-size-xs);
}

.mobile-single .barcode-overlay {
  display: none;
}

/* Mobile 2 per row: vertical layout */
.mobile-double {
  min-height: 380px;
  height: auto;
}

.mobile-double .card-image-wrapper {
  height: 170px;
}

.mobile-double .card-name {
  font-size: var(--font-size-base);
  padding: var(--spacing-xs) var(--spacing-sm);
}

.mobile-double .card-stats {
  padding: 0 var(--spacing-xs) var(--spacing-xs);
  gap: 1px;
}

.mobile-double .stat-row {
  font-size: 10px;
  justify-content: flex-start;
}

.mobile-double .bottom-row {
  padding: var(--spacing-sm) 0;
}

.mobile-double .price-box {
  padding: 6px 10px;
}

.mobile-double .price-box-original {
  padding: 6px 10px;
}

.mobile-double .price-currency {
  font-size: 10px;
}

.mobile-double .price-value {
  font-size: var(--font-size-base);
}

.mobile-double .add-to-cart-btn {
  padding: var(--spacing-md) var(--spacing-sm);
  font-size: 12px;
}

/* ============================================
   Tablet & Mobile Hover/Active Adjustments
   ============================================ */
.tablet-single:hover,
.tablet-double:hover,
.tablet-triple:hover,
.mobile-single:hover,
.mobile-double:hover {
  transform: none;
  box-shadow: none;
}

.tablet-single:active,
.tablet-double:active,
.tablet-triple:active,
.mobile-single:active,
.mobile-double:active {
  transform: scale(0.98);
  background: var(--bg-secondary);
}

/* ============================================
   Grid Mode (no gaps, borders only)
   ============================================ */
.item-card.grid-mode {
  border-radius: 0 !important;
  border: none !important;
  border-bottom: 1px solid var(--border-color) !important;
  border-left: 1px solid var(--border-color) !important;
}

.item-card.grid-mode:hover {
  transform: none !important;
  box-shadow: none !important;
  background: var(--bg-secondary);
}

.item-card.grid-mode:active {
  transform: none !important;
  background: var(--bg-tertiary);
}

/* ============================================
   Out of Stock Styles
   ============================================ */
.item-card.out-of-stock {
  opacity: 0.7;
}

.item-card.out-of-stock .card-image {
  filter: grayscale(0.5);
}

.item-card.out-of-stock .card-image-placeholder {
  filter: grayscale(0.5);
}

.out-of-stock-label {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-md);
  background: rgba(239, 68, 68, 0.08);
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: var(--border-radius);
  color: #dc2626;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  text-align: center;
}

/* ============================================
   Not In Assortment Styles
   ============================================ */
.item-card.not-in-assortment {
  opacity: 0.65;
  border-style: dashed;
  border-color: var(--color-gray-300, #cbd5e1);
  cursor: default;
}

.item-card.not-in-assortment:hover {
  transform: none;
  box-shadow: none;
}

.not-in-assortment-label {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-md);
  background: rgba(100, 116, 139, 0.08);
  border: 1px dashed var(--color-secondary, #64748b);
  border-radius: var(--border-radius);
  color: var(--color-secondary, #64748b);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  text-align: center;
}

/* Make non-selected NIA cards show pointer cursor (now clickable) */
.item-card.not-in-assortment {
  cursor: pointer;
}

/* Selected NIA card — green border, restore opacity, pointer cursor */
.item-card.not-in-assortment.nia-selected {
  opacity: 1;
  border-style: solid;
  border-color: var(--color-success);
  cursor: pointer;
}

.item-card.not-in-assortment.nia-selected:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.25);
}

/* Checkmark badge */
.nia-selection-check {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  background: var(--color-success);
  border-radius: 50%;
  box-shadow: 0 2px 6px rgba(16, 185, 129, 0.4);
}

/* Pop animation */
.check-pop-enter-active {
  animation: nia-check-pop 0.25s ease-out;
}
.check-pop-leave-active {
  animation: nia-check-pop 0.15s ease-in reverse;
}
@keyframes nia-check-pop {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  70% {
    transform: scale(1.15);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}
</style>
